import { useEffect, useRef, useState } from "react";
import {
  Banknote,
  Copy,
  Check,
  ShieldCheck,
  HandHeart,
  Mail,
  Lock,
  Flag,
  Sparkles,
  Heart,
} from "lucide-react";

/* -------------------------------------------------------------------------- */
/*  PayPal Hosted Button — fresh credentials                                  */
/* -------------------------------------------------------------------------- */
const PAYPAL_HOSTED_BUTTON_ID = "YXMLE5RJ7EHXA";
const PAYPAL_CONTAINER_ID = `paypal-container-${PAYPAL_HOSTED_BUTTON_ID}`;
const PAYPAL_SCRIPT_ID = "paypal-sdk-hosted-buttons";
// Exact SDK URL — `components=hosted-buttons` is mandatory so that
// `window.paypal.HostedButtons` exists; without it the SDK falls through
// to Smart-Buttons which throws "Expected an order id to be passed".
const PAYPAL_SDK_URL =
  "https://www.paypal.com/sdk/js?client-id=BAAqJHAFKWqHtr6mHKVWQxE6NZ5YzY4Ra10B4x0K7t-PMXBwSfP5PcAJVElYwFJ7cM9Jx6stdTk2MPMkVE&components=hosted-buttons&disable-funding=venmo&currency=NZD";

/* -------------------------------------------------------------------------- */
/*  Bank transfer details — NZ regional                                       */
/* -------------------------------------------------------------------------- */
const BANK = {
  account_name: "Life Talk Trust",
  bank_name: "ANZ Bank",
  account_number: "06-0491-0212963-00",
  particulars: "Tithe / Offering",
  reference: "LifeTalk NZ",
};

const RECEIPT_EMAIL = "giving@fathershousetauranga.nz";

/* -------------------------------------------------------------------------- */
/*  Reusable "copy on click" row                                              */
/* -------------------------------------------------------------------------- */
function CopyRow({ label, value, copyable = false, testid }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!copyable) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const el = document.createElement("textarea");
      el.value = value;
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand("copy");
      } catch {
        /* no-op */
      } finally {
        document.body.removeChild(el);
      }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div
      data-testid={testid}
      className="group grid grid-cols-12 items-center gap-3 px-5 sm:px-7 py-5 hover:bg-white/[0.025] transition-colors"
    >
      <dt className="col-span-12 sm:col-span-4 text-[10px] tracking-[0.28em] uppercase text-[#C5D1EB]/70">
        {label}
      </dt>
      <dd className="col-span-10 sm:col-span-7 font-display text-white text-base sm:text-lg tracking-tight break-all">
        {value}
      </dd>
      {copyable && (
        <button
          type="button"
          onClick={handleCopy}
          data-testid={`${testid}-copy`}
          aria-label={`Copy ${label}`}
          aria-live="polite"
          className={`col-span-2 sm:col-span-1 ml-auto inline-flex items-center justify-center w-9 h-9 border transition-all ${
            copied
              ? "border-[#C5001A] bg-[#C5001A] text-white"
              : "border-[#C5D1EB]/25 text-[#C5D1EB] hover:border-[#C5001A] hover:text-white hover:bg-[#C5001A]/15"
          }`}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Premium Direct Bank Transfer card                                         */
/* -------------------------------------------------------------------------- */
function BankTransferCard() {
  const [copiedAll, setCopiedAll] = useState(false);

  const copyAll = async () => {
    const text =
      `Account Name: ${BANK.account_name}\n` +
      `Bank: ${BANK.bank_name}\n` +
      `Account Number: ${BANK.account_number}\n` +
      `Particulars: ${BANK.particulars}\n` +
      `Reference: ${BANK.reference}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* swallow */
    }
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2200);
  };

  return (
    <article
      data-testid="bank-transfer-card"
      className="relative overflow-hidden bg-white/[0.04] backdrop-blur-xl border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
    >
      {/* Ambient red glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full opacity-35 blur-[120px]"
        style={{ background: "radial-gradient(circle, #C5001A, transparent 70%)" }}
      />
      {/* Subtle bottom-left wash */}
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-24 -left-24 w-[22rem] h-[22rem] rounded-full opacity-20 blur-[120px]"
        style={{ background: "radial-gradient(circle, #E31C3D, transparent 70%)" }}
      />

      <div className="relative px-8 sm:px-12 pt-10 sm:pt-12 pb-10 sm:pb-12">
        {/* Header */}
        <div className="flex items-start justify-between gap-6">
          <div>
            <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#C5D1EB]/75">
              <span
                className="inline-block w-1.5 h-1.5 bg-[#C5001A]"
                aria-hidden
              />
              New Zealand · NZD
            </span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-[1.05]">
              Direct Bank Transfer
            </h2>
            <p className="mt-4 text-[#C5D1EB] text-sm sm:text-base leading-relaxed max-w-xl">
              The cleanest way to give from inside New Zealand — zero
              processing fees, instant credit, and full traceability for your
              tax receipts.
            </p>
          </div>
          <span
            aria-hidden
            className="hidden sm:inline-flex shrink-0 w-14 h-14 items-center justify-center border border-[#C5001A]/60 bg-[#C5001A]/15 text-[#C5001A]"
          >
            <Banknote size={22} />
          </span>
        </div>

        {/* Details list */}
        <dl
          data-testid="bank-details"
          className="mt-10 border border-white/10 divide-y divide-white/10 bg-[#0B0C10]/55"
        >
          <CopyRow
            label="Account Name"
            value={BANK.account_name}
            testid="bank-row-account-name"
          />
          <CopyRow
            label="Bank"
            value={BANK.bank_name}
            testid="bank-row-bank"
          />
          <CopyRow
            label="Account Number"
            value={BANK.account_number}
            copyable
            testid="bank-row-account-number"
          />
          <CopyRow
            label="Particulars"
            value={BANK.particulars}
            copyable
            testid="bank-row-particulars"
          />
          <CopyRow
            label="Reference"
            value={BANK.reference}
            copyable
            testid="bank-row-reference"
          />
        </dl>

        {/* Primary action */}
        <button
          type="button"
          onClick={copyAll}
          data-testid="copy-all-details"
          aria-live="polite"
          className="mt-8 w-full inline-flex items-center justify-center gap-2.5 bg-[#C5001A] hover:bg-[#E31C3D] text-white px-7 py-4 text-sm font-medium tracking-wide transition-colors shadow-[0_10px_40px_rgba(197,0,26,0.35)]"
        >
          {copiedAll ? <Check size={16} /> : <Copy size={16} />}
          {copiedAll
            ? "All bank details copied to clipboard"
            : "Copy all bank details"}
        </button>

        {/* Tax receipt note */}
        <div className="mt-7 flex items-start gap-3 text-[12px] text-[#C5D1EB]/85 leading-relaxed">
          <Mail
            size={14}
            className="mt-0.5 text-[#C5001A] shrink-0"
            aria-hidden
          />
          <p>
            Please include the <span className="text-white">Particulars</span>{" "}
            and <span className="text-white">Reference</span> so we can
            allocate your gift to the right ministry. For a tax receipt, email{" "}
            <a
              href={`mailto:${RECEIPT_EMAIL}`}
              data-testid="bank-receipt-email"
              className="text-white underline decoration-[#C5001A]/60 underline-offset-4 hover:decoration-[#C5001A]"
            >
              {RECEIPT_EMAIL}
            </a>
            .
          </p>
        </div>
      </div>
    </article>
  );
}

/* -------------------------------------------------------------------------- */
/*  Trust-strip — 4 reassurance badges below the card                          */
/* -------------------------------------------------------------------------- */
const TRUST_BADGES = [
  {
    Icon: ShieldCheck,
    title: "Privacy Act 2020",
    body: "Your details handled in line with NZ Privacy Act.",
  },
  {
    Icon: Lock,
    title: "Bank-grade transfer",
    body: "Direct ANZ bank credit — no third-party processors.",
  },
  {
    Icon: Flag,
    title: "Registered NZ trust",
    body: "Life Talk Trust — Tauranga, Bay of Plenty.",
  },
  {
    Icon: HandHeart,
    title: "100% to ministry",
    body: "Funds sustain the 24/7 broadcast & local outreach.",
  },
];

/* -------------------------------------------------------------------------- */
/*  PayPal Hosted Button card                                                  */
/*                                                                            */
/*  Clean React useEffect lifecycle:                                          */
/*   1. Dynamically appends the SDK <script> to <head> exactly once           */
/*      (singleton by id), with crossorigin="anonymous" and async.            */
/*   2. As soon as the SDK has loaded — or if `window.paypal.HostedButtons`   */
/*      is already available from an earlier mount — invokes                  */
/*        window.paypal.HostedButtons({ hostedButtonId })                     */
/*          .render(`#paypal-container-${hostedButtonId}`)                    */
/*   3. On unmount the container's innerHTML is cleared so React can't        */
/*      collide with PayPal's injected DOM on next mount.                     */
/* -------------------------------------------------------------------------- */
function PaypalCard() {
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    // Render block — kept verbatim to the integration spec.  The guard
    // `window.paypal && window.paypal.HostedButtons` ensures we never call
    // through the default Smart-Buttons API (which would throw
    // "Expected an order id to be passed").
    const renderHostedButton = () => {
      if (cancelled) return;
      const container = containerRef.current;
      if (!container) return;
      container.innerHTML = "";
      if (window.paypal && window.paypal.HostedButtons) {
        try {
          window.paypal
            .HostedButtons({
              hostedButtonId: "YXMLE5RJ7EHXA",
            })
            .render("#paypal-container-YXMLE5RJ7EHXA");
        } catch {
          /* swallow — UI shows an empty container, secondary path remains */
        }
      }
    };

    // SDK already on the page → render immediately.
    if (window.paypal && window.paypal.HostedButtons) {
      renderHostedButton();
      return () => {
        cancelled = true;
        if (containerRef.current) containerRef.current.innerHTML = "";
      };
    }

    // Otherwise dynamically inject the SDK <script> exactly once.
    // Dedup by EXACT src attribute (spec) — fall back to the well-known
    // id so we still catch stale tags from earlier client-id sessions.
    let script =
      document.querySelector(`script[src="${PAYPAL_SDK_URL}"]`) ||
      document.getElementById(PAYPAL_SCRIPT_ID);
    if (!script) {
      script = document.createElement("script");
      script.id = PAYPAL_SCRIPT_ID;
      script.src = PAYPAL_SDK_URL;
      script.async = true;
      script.crossOrigin = "anonymous";
      document.head.appendChild(script);
    }
    script.addEventListener("load", renderHostedButton);

    return () => {
      cancelled = true;
      script.removeEventListener("load", renderHostedButton);
      if (containerRef.current) containerRef.current.innerHTML = "";
    };
  }, []);

  return (
    <article
      data-testid="paypal-card"
      className="relative overflow-hidden bg-white/[0.04] backdrop-blur-xl border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] flex flex-col"
    >
      {/* Ambient red glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -left-32 w-[26rem] h-[26rem] rounded-full opacity-30 blur-[120px]"
        style={{ background: "radial-gradient(circle, #C5001A, transparent 70%)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-24 -right-24 w-[20rem] h-[20rem] rounded-full opacity-20 blur-[120px]"
        style={{ background: "radial-gradient(circle, #0070BA, transparent 70%)" }}
      />

      <div className="relative px-8 sm:px-12 pt-10 sm:pt-12 pb-10 sm:pb-12 flex flex-col h-full">
        {/* Header */}
        <div className="flex items-start justify-between gap-6">
          <div>
            <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#C5D1EB]/75">
              <span
                className="inline-block w-1.5 h-1.5 bg-[#C5001A]"
                aria-hidden
              />
              International · NZD
            </span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-[1.05]">
              Give with PayPal
            </h2>
            <p className="mt-4 text-[#C5D1EB] text-sm sm:text-base leading-relaxed max-w-xl">
              One-tap giving for overseas partners and listeners. Tithes,
              offerings and missions seed — settled directly to Life Talk
              Trust in New Zealand Dollars.
            </p>
          </div>
          <span
            aria-hidden
            className="hidden sm:inline-flex shrink-0 w-14 h-14 items-center justify-center border border-[#C5001A]/60 bg-[#C5001A]/15 text-[#C5001A]"
          >
            <Heart size={22} />
          </span>
        </div>

        {/* PayPal mount surface */}
        <div className="mt-10 flex-1 flex flex-col items-center justify-center border border-white/10 bg-[#0B0C10]/55 p-5 sm:p-7">
          <div
            ref={containerRef}
            id={PAYPAL_CONTAINER_ID}
            data-testid="paypal-container"
            className="w-full max-w-md mx-auto flex flex-col items-stretch justify-center"
            style={{ minHeight: "180px" }}
          />
        </div>

        {/* Footer trust line */}
        <p className="mt-7 text-[11px] text-[#C5D1EB]/65 flex items-center gap-2">
          <ShieldCheck size={13} className="text-[#C5D1EB]/75" />
          Processed securely by PayPal · NZD · funds settle to Life Talk Trust
        </p>
      </div>
    </article>
  );
}


function TrustStrip() {
  return (
    <ul
      data-testid="giving-trust-strip"
      className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3"
    >
      {TRUST_BADGES.map(({ Icon, title, body }) => (
        <li
          key={title}
          className="bg-white/[0.03] backdrop-blur-md border border-white/10 p-5 flex items-start gap-3"
        >
          <span className="shrink-0 w-9 h-9 flex items-center justify-center bg-[#C5001A]/15 border border-[#C5001A]/50 text-[#C5001A]">
            <Icon size={15} />
          </span>
          <div className="min-w-0">
            <p className="font-display text-white text-sm tracking-tight">
              {title}
            </p>
            <p className="mt-1 text-xs text-[#C5D1EB] leading-relaxed">
              {body}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */
export default function Giving() {
  return (
    <main data-testid="giving-page" className="bg-canvas">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-40 right-1/4 w-[44rem] h-[44rem] rounded-full opacity-20 blur-[140px]"
          style={{
            background: "radial-gradient(circle, #C5001A, transparent 70%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 lg:pt-28 pb-12">
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#C5D1EB]/70 inline-flex items-center gap-2">
            <Sparkles size={13} className="text-[#C5001A]" />
            Giving
          </span>
          <h1 className="mt-5 font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.04] max-w-3xl">
            Sow into the broadcast & the house.
          </h1>
          <p className="mt-6 text-[#C5D1EB] text-base sm:text-lg max-w-2xl leading-relaxed">
            Tithes, offerings and missions seed sustain the 24/7 stream, the
            Tauranga assembly, and our outreach to migrant families across the
            Bay of Plenty. Every dollar is honoured before the Lord.
          </p>
        </div>
      </section>

      {/* Bank details + PayPal — twin glassmorphic cards */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-14">
        <div className="grid lg:grid-cols-2 gap-6 items-stretch">
          <BankTransferCard />
          <PaypalCard />
        </div>
      </section>

      {/* Trust strip */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-24">
        <TrustStrip />
      </section>
    </main>
  );
}
